import { getEnv } from "../../AppConfigs";
import { documentStatus } from "../../constants/statusConstants";
import { FileHelper } from "../../helper";
import { addYear, getDay, setUtcTime } from "../../helper/dateTimeHelper";
import { IFileFields, IFileInfo } from "../../models/IFileInfo";
import api from "../axiosBase";
import { StatisticsEmployeeService } from "../department-viags/statistics-employee.service";
import { DocumentService } from "../document/document.service";
import { RequestError } from "../requestError";
import { getURL } from "../serviceHelper";
import {
  buildPeopleJsonFromBuildData,
  getFileExt,
  getFileNameWithoutExt,
  getFolderFromPath,
  getParentSubFolderPathFromSharePoint,
} from "../util";
import { MsGraphFile } from "./MsGraphFile";

const SPSiteId = getEnv("REACT_APP_SP_SITE_ID");
const DefaultSPListId = getEnv("REACT_APP_FOLDER_AND_FILE_SP_LIST_ID");
const SP_PUBLISH_DRIVE_ID = getEnv("REACT_APP_PUBLIC_FOLDER_AND_FILE_SP_DRIVE_ID");

const select =
  "$select=id,name,webUrl,docInfoId,docWorkingId,docAreas,docPublicDepartment,docPublicDepartmentLookupId,IsCustomEditor,Titlee,subInfo,docNotes,docTypeLookupId,docType,docOrder,docStatus,docStatusId,docStatusValue,docPublicVersion,docPublishViewVersion,docEditionVersion,eTag,createdBy,parentReference,createdByUser,createdDateTime,sharepointIds,lastModifiedBy,lastModifiedByUser,lastModifiedDateTime,file,folder,Tags,expiredDate,FolderChildCount,effectiveDate";
const orderbyQuery = "$orderby=lastModifiedDateTime";
const expandQuery = "$expand=listItem($expand=fields)";

const getUploadUrl = (
  size: "small" | "large",
  { folderId, listId, fileName }: { folderId?: string; listId?: string; fileName: string },
) => {
  let url = "";
  // upload small file url
  if (size === "small") {
    url = `/sites/${SPSiteId}/lists/${listId}/drive/root:/${fileName}:/content`;
  }
  if (size === "small" && folderId) {
    url = `/sites/${SPSiteId}/lists/${listId}/drive/items/${folderId}:/${fileName}:/content`;
  }
  // upload large file url
  if (size === "large") {
    url = `/sites/${SPSiteId}/lists/${listId}/drive/root:/${fileName}:/createUploadSession`;
  }
  if (size === "large" && folderId) {
    url = `/sites/${SPSiteId}/lists/${listId}/drive/items/${folderId}:/${fileName}:/createUploadSession`;
  }
  return getURL(url);
};

export class FileService {
  static async getChild(folderId: string, listId?: string, fileOnly?: boolean, keyword?: string, docTypeId?: string) {
    return this.getFilesInFolder(folderId, listId, fileOnly, keyword, docTypeId);
  }

  static async getRootFolder(listId?: string, fileOnly?: boolean, keyword?: string, docTypeId?: string) {
    return this.getFilesInFolder("root", listId, fileOnly, keyword, docTypeId);
  }

  static async getFilesInFolder(
    folderId: string,
    listId?: string,
    fileOnly?: boolean,
    keyword?: string,
    docTypeId?: string,
  ) {
    if (!listId) {
      listId = DefaultSPListId;
    }

    let folderCondition = "root";
    if (folderId !== "root") {
      folderCondition = `items/${folderId}`;
    }

    let url = `/sites/${SPSiteId}/lists/${listId}/drive/${folderCondition}/children?${select}&${orderbyQuery}&${expandQuery}`;

    const filters = [];
    if (keyword && keyword.length > 0) {
      filters.push(`startswith(name,'${keyword}')`);
    }
    /* 
    if(docTypeId) {
      filters.push(`docTypeLookupId eq '${docTypeId}'`);
    } */

    if (filters.length > 0) {
      url = url + `&$filter=(${filters.join(" and ")})`;
    }

    return api
      .get(getURL(url))
      .then((res) => {
        if (!fileOnly) {
          const child = res.data.value.map((item: any) => this.mapData(item));
          return {
            data: child,
            count: child.length,
          };
        }

        let filtered = [];
        if (fileOnly) {
          filtered = res.data.value.filter((o: any) => o.file);
        }

        if (docTypeId) {
          filtered = filtered.filter((o: any) => o.listItem?.fields?.Tags?.includes(docTypeId));
        }

        const child = filtered.map((item: any) => this.mapData(item));

        return {
          data: child,
          count: child.length,
        };
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async getFile(id: any, listId?: string) {
    if (!listId) {
      listId = DefaultSPListId;
    }

    let url = `/sites/${SPSiteId}/lists/${listId}/drive/items/${id}?${select}&&${expandQuery}`;

    return api
      .get(getURL(url))
      .then((res) => {
        return this.mapData(res.data);
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async createFile(file: any, folderId: any, listId?: string, fields: any = null) {
    if (!listId) listId = DefaultSPListId;

    let url = `/sites/${SPSiteId}/lists/${listId}/drive/root:/${file.name}:/content`;
    if (folderId) {
      url = `/sites/${SPSiteId}/lists/${listId}/drive/items/${folderId}:/${file.name}:/content`;
    }
    return api
      .put(getURL(url), file.content)
      .then((res) => {
        return this.mapData(res.data);
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async createFileWithFolderPath(file: any, folderPath: any, listId?: string, fields: any = null) {
    if (!listId) listId = DefaultSPListId;

    let url = `/sites/${SPSiteId}/lists/${listId}/drive/root:/${file.name}:/content`;
    if (folderPath) {
      url = `/sites/${SPSiteId}/lists/${listId}/drive/root:/${folderPath}/${file.name}:/content`;
    }
    return api
      .put(getURL(url), file.content)
      .then((res) => {
        return this.mapData(res.data);
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async updateFile(file: any, itemId: any, listId?: string, fields: any = null) {
    if (!listId) listId = DefaultSPListId;
    let url = `/sites/${SPSiteId}/lists/${listId}/drive/items/${itemId}/content`;
    return api
      .put(getURL(url), file.content)
      .then((res) => {
        return this.mapData(res.data);
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async createFileDocumentInfo(
    msgFileInfo: any,
    folderId: any,
    documentFields: any = null,
    filelistId?: string,
    infolistId?: string,
  ) {
    if (!folderId || folderId === "") {
      folderId = "root";
    }

    const fileInfo = await this.getFile(msgFileInfo.id, filelistId);
    if (fileInfo) {
      const editors = buildPeopleJsonFromBuildData([msgFileInfo.createdUser]);

      const extension = getFileExt(fileInfo.name)?.toLowerCase();

      let title = documentFields?.Title;
      if (!title || title === "") {
        title = fileInfo.nameWithoutExt;
      }

      const fields = {
        ...documentFields,
        fileId: fileInfo.id,
        docId: fileInfo.listItemId,
        Title: FileHelper.extractFileName(title),
        docUrl: fileInfo.url,
        docStatusId: documentFields?.docStatusId ?? documentStatus.new.id,
        docStatusValue: documentFields?.docStatusValue ?? documentStatus.new.value,
        docStatus: documentFields?.docStatus ?? documentStatus.new.label,
        docNotes: documentFields?.docNotes ?? "Khởi tạo tài liệu",
        docEditors: documentFields?.docEditors ?? editors,
        fileAssignedTo: documentFields?.fileAssignedTo,
        IsConvertToPDF: documentFields?.IsConvertToPDF ?? extension !== "pdf",
        fileAttachment: documentFields?.fileAttachment,
      };

      const documentInfo = await DocumentService.createFileInfo(fields, infolistId);
      if (documentInfo) {
        const statisticFields = {
          docId: +fileInfo.listItemId,
          docCode: documentFields?.docCode,
          docName: documentFields?.Title ?? documentInfo.Title,
          docType: "Nội bộ",
          docStatus: documentFields?.docStatus ?? documentStatus.new.label,
          publishDay: setUtcTime(getDay(new Date())),
          effectiveDate: setUtcTime(getDay(new Date())),
        };
        await StatisticsEmployeeService.addDocForUsers(statisticFields);
        await FileService.updateFileInfoFromDocumentInfo(documentInfo.docId, fields, filelistId, fileInfo);
      }
      return documentInfo;
    }
    return null;
  }

  static async updateFileInfoFromDocumentInfo(docId: any, fields: any, listId?: any, fileInfo?: any) {
    const fileInfoFields: IFileFields = {
      docTypeLookupId: fields.docTypeLookupId,
      docPublicDepartmentLookupId: fields.docPublicDepartmentLookupId,
      subInfo: fields.subInfo,
      docNotes: fields.docNotes,
      docPublicVersion: fields.docPublicVersion,
      docEditionVersion: fields.docEditionVersion,
      docPublishViewVersion: fields.docPublishViewVersion,
      docCode: fields.docCode,
      docOrder: fields.docOrder,
      docStatusId: fields.docStatusId,
      docStatusValue: fields.docStatusValue,
      docStatus: fields.docStatus,
      Title: fields.Title,
      Tags: fields.Tags,
      docWorkingId: fields.docWorkingId,
      workingFileId: fields.workingFileId,
      expiredDate: fields.effectiveTo || addYear(fileInfo?.createdDateTime, 1),
      docEditTemp: fields.docEditTemp,
      docReaders: undefined,
    };

    return await FileService.updateFileInfo(docId, fileInfoFields, listId);
  }

  static async updateDoc({ docId, fields, listId }: { docId: string; fields: any; listId?: string }) {
    return await FileService.updateFileInfo(docId, fields, listId);
  }

  static async convertFile(fileId: any, listId?: any, format: any = "pdf") {
    if (!listId) {
      listId = DefaultSPListId;
    }

    let url = `/sites/${SPSiteId}/lists/${listId}/drive/items/${fileId}/content?format=${format}`;

    return api
      .get(getURL(url))
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static uploadFile(file: any, folderId: string, listId: string = DefaultSPListId) {
    const MAX_SMALL_FILE_SIZE = 4194304; // 4 MiB
    const url = getUploadUrl(file.size < MAX_SMALL_FILE_SIZE ? "small" : "large", {
      listId,
      folderId,
      fileName: file.name,
    });
    const msGraphFile = new MsGraphFile(file, url);
    return msGraphFile.upload();
  }

  static async getDriveItem(params: { listId?: string; itemId: string }) {
    const listId = params?.listId ?? DefaultSPListId;
    const url = getURL(`/sites/${SPSiteId}/lists/${listId}/drive/items/${params.itemId}?expand=listItem`);
    return api.get(url);
  }

  static async addFolder(folder: any, parentId: string, listId?: string) {
    if (!listId) listId = DefaultSPListId;
    return api
      .post(getURL(`/sites/${SPSiteId}/lists/${listId}/drive/items/${parentId}/children`), folder)
      .then((res) => res.data)
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async updateFolderName(id: any, fields: any, listId?: string) {
    if (!listId) listId = DefaultSPListId;

    return api
      .patch(getURL(`/sites/${SPSiteId}/lists/${listId}/items/${id} `), fields)
      .then((res) => {
        if (!res) return null;
        return res.data;
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async updateFolderNamByDriveId(driveId: string, fields: any) {
    return api
      .patch(getURL(`/sites/${SPSiteId}/drives/${SP_PUBLISH_DRIVE_ID}/items/${driveId} `), fields)
      .then((res) => {
        if (!res) return null;
        return res.data;
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async getFolderByPath(path: string) {
    const url = getURL(`/sites/${SPSiteId}/drives/${SP_PUBLISH_DRIVE_ID}/root:/${path}`);
    try {
      const res = await api.get(url);
      return res.data;
    } catch (err) {
      console.log(err);
    }
  }

  static async deletedFile(uniqueId: any, listId?: string) {
    if (!listId) listId = DefaultSPListId;

    return api
      .delete(getURL(`/sites/${SPSiteId}/lists/${listId}/drive/items/${uniqueId}`))
      .then((res) => res.data)
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async updateFileInfo(id: any, fields: any, listId?: string) {
    if (!listId) listId = DefaultSPListId;

    return api
      .patch(getURL(`/sites/${SPSiteId}/lists/${listId}/items/${id}/fields`), fields)
      .then((res) => {
        return this.mapDataFromUpdate(id, res.data);
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static mapDataFromUpdate(fileId: any, item: any) {
    return {
      id: fileId,
      docInfoId: item.docInfoId,
      Tags: item.Tags ? JSON.parse(item.Tags) : [],
      docAreas: item.docAreas ? JSON.parse(item.docAreas) : [],
      subInfo: item.subInfo,
      listItemId: item.id,
      Title: item.Title,
      docStatusId: item.docStatusId,
      docTypeId: item.docTypeLookupId,
      docType: item.docType,
      docStatus: item.docStatus,
      docStatusValue: item.docStatusValue,
      docEditionVersion: item.docEditionVersion,
      docCode: item.docCode,
      docPublicVersion: item.docPublicVersion,
      docPublishViewVersion: item.docPublishViewVersion,
      docWorkingId: item.docWorkingId,
      name: item.name,
      nameWithoutExt: item.name ? getFileNameWithoutExt(item.name) : "",
      createdDateTime: item.createdDateTime,
      lastModifiedDateTime: item.lastModifiedDateTime,
      type: item?.name ? getFileExt(item?.name) : "",
      isFolder: item.folder ? true : false,
      url: item.webUrl,
      files: [],
      parentId: item.parentReference?.id,
      parentDriveId: item.parentReference?.driveId,
      folder: getFolderFromPath(item.parentReference?.path),
      parentSubFolderPath: getParentSubFolderPathFromSharePoint(item.parentReference?.path),
      docPublicDepartment: item.docPublicDepartment,
      IsCustomEditor: item.IsCustomEditor,
      createdUser: item.createdBy?.user,
      modifiedUser: item.lastModifiedBy?.user,
      expiredDate: item.expiredDate,
      docEditTemp: item.docEditTemp,
    } as IFileInfo;
  }

  static mapData(item: any) {
    return {
      id: item.id,
      docInfoId: item.listItem?.fields?.docInfoId,
      Tags: item.listItem?.fields?.Tags ? JSON.parse(item.listItem?.fields?.Tags) : [],
      docAreas: item.listItem?.fields?.docAreas ? JSON.parse(item.listItem?.fields?.docAreas) : [],
      subInfo: item.listItem?.fields?.subInfo,
      listItemId: item.sharepointIds?.listItemId,
      Title: item.listItem?.fields?.Title,
      docStatusId: item.listItem?.fields?.docStatusId,
      docTypeId: item.listItem?.fields?.docTypeLookupId,
      docType: item.listItem?.fields?.docType,
      docStatus: item.listItem?.fields?.docStatus,
      docStatusValue: item.listItem?.fields?.docStatusValue,
      docEditionVersion: item.listItem?.fields?.docEditionVersion,
      docCode: item.listItem?.fields?.docCode,
      docPublicVersion: item.listItem?.fields?.docPublicVersion,
      docPublishViewVersion: item.listItem?.fields?.docPublishViewVersion,
      docWorkingId: item.listItem?.fields?.docWorkingId,
      docOrder: item.listItem?.fields?.docOrder,
      name: item.name,
      nameWithoutExt: getFileNameWithoutExt(item.name),
      createdDateTime: item.createdDateTime,
      lastModifiedDateTime: item.lastModifiedDateTime,
      type: getFileExt(item.name),
      isFolder: item.folder ? true : false,
      url: item.webUrl,
      files: [],
      parentId: item.parentReference?.id,
      parentDriveId: item.parentReference?.driveId,
      folder: getFolderFromPath(item.parentReference?.path),
      parentSubFolderPath: getParentSubFolderPathFromSharePoint(item.parentReference?.path),
      docPublicDepartment: item.listItem?.fields?.docPublicDepartment,
      docPublicDepartmentLookupId: item.listItem?.fields?.docPublicDepartmentLookupId,
      IsCustomEditor: item.listItem?.fields?.IsCustomEditor,
      createdUser: item.createdBy?.user,
      modifiedUser: item.lastModifiedBy?.user,
      expiredDate: item.listItem?.fields?.expiredDate,
      FolderChildCount: +item.listItem?.fields?.FolderChildCount,
      docEditTemp: item.listItem?.fields?.docEditTemp,
      effectiveDate: item.listItem?.fields?.effectiveDate,
    } as IFileInfo;
  }
}
